// Holding a quick reaction (#726): the hover bar's emoji, the More menu's recent row and the phone sheet's row. They work as
// the picker's cells do (#714): under 0.45 s a tap (the button's own click), 0.45 to 2 s a super reaction, 2 to 6 s a jumbo
// one, 6 s a splat that sends nothing. A mouse grows the emoji in place; a finger covers it, so the overlay draws it in a
// bubble above the button (HoldOverlay). A hold that sent or splatted swallows the click that follows it.
import { useEffect, useMemo, useRef } from "react";
import { HoldDriver, browserFlags } from "../jumboHold.ts";
import { getReactionEffects } from "../reactionEffects.ts";
import { playSplat, primeJumboSounds } from "../jumboSound.ts";
import { HoldOverlay } from "./HoldOverlay.tsx";

const SWALLOW_CLICK_MS = 100;

export type QuickHeld = "super" | "jumbo";

export interface QuickHold {
  /** The pointer handlers for the button that shows `emoji`; `onHeld` says what a super or jumbo hold on it does. */
  bind: (emoji: string, onHeld: (how: QuickHeld) => void) => {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
    onContextMenu: (e: React.MouseEvent) => void;
    /** A finger on the button is not the sheet's drag-down (a drift as it looks at the bubble would pull the sheet). */
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
  };
  /** True once after a hold that sent or splatted: the click that follows it is not a tap. */
  swallowClick: () => boolean;
}

export function useQuickHold(): { hold: QuickHold; overlay: React.ReactNode } {
  /** What the pressed button does with a super or jumbo hold: set at the press. */
  const pressed = useRef<((how: QuickHeld) => void) | null>(null);
  const swallow = useRef(false);
  const driver = useMemo(
    () =>
      HoldDriver.browser({
        flags: browserFlags(getReactionEffects),
        onSplat: playSplat,
        // "normal" is a tap: the button's click does it.
        onSend: (_target, send) => send !== "normal" && pressed.current?.(send),
      }),
    [],
  );
  useEffect(() => () => driver.cancel(), [driver]);
  const hold = useMemo<QuickHold>(
    () => ({
      bind: (emoji, onHeld) => ({
        onPointerDown: (e) => {
          if (e.pointerType === "mouse" && e.button !== 0) return;
          swallow.current = false;
          pressed.current = onHeld;
          primeJumboSounds();
          driver.press({ el: e.currentTarget, emoji, touch: e.pointerType !== "mouse" });
        },
        onPointerMove: (e) => {
          driver.moved(e.clientX, e.clientY);
        },
        onPointerUp: (e) => {
          // A touch keeps its pointer after it leaves the button: let go off the button (past the slop) is a leave.
          if (!driver.moved(e.clientX, e.clientY)) return;
          const was = driver.release();
          if (was === "none" || was === "normal") return;
          swallow.current = true;
          setTimeout(() => (swallow.current = false), SWALLOW_CLICK_MS);
        },
        onPointerLeave: (e) => {
          if (e.pointerType === "mouse") driver.leave();
        },
        onPointerCancel: () => driver.cancel(),
        onContextMenu: (e) => e.preventDefault(),
        onTouchStart: (e) => e.stopPropagation(),
        onTouchMove: (e) => e.stopPropagation(),
      }),
      swallowClick: () => {
        const was = swallow.current;
        swallow.current = false;
        return was;
      },
    }),
    [driver],
  );
  return { hold, overlay: <HoldOverlay driver={driver} /> };
}
